let Stardust = {};

(function registerStardustFormatter(root, createFormatter) {
  // Assigned unconditionally. The `if (root)` guard was a classic-script
  // artefact: with no window there was no namespace to register on, so it
  // skipped. As an ESM module the export exists regardless of environment, and
  // the formatter is pure — it reads a rate and formats numbers. Leaving the
  // guard meant `require()` from a NODE-environment test returned an empty
  // object, which is how the client/server money-equivalent parity suite lost
  // its client half.
  Stardust = createFormatter(root);
})(typeof window === 'undefined' ? null : window, (root) => {
  const suffixes = ['', 'K', 'M', 'B', 'T', 'Q'];

  function normalize(value) {
    if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return null;
    const numeric = Number(value);
    return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : null;
  }

  function format(value) {
    const numeric = normalize(value);
    if (numeric === null) return '—';
    if (numeric < 1_000) return String(numeric);

    const digits = String(numeric);
    const unit = Math.min(Math.floor((digits.length - 1) / 3), suffixes.length - 1);
    const wholeDigits = ((digits.length - 1) % 3) + 1;
    const decimalPlaces = 3 - wholeDigits;
    const truncated = Number(digits.slice(0, 3)) / (10 ** decimalPlaces);
    return `${truncated}${suffixes[unit]}`;
  }

  /* ---------- real-money equivalent ----------
     The CPC Network's key principles on in-game virtual currencies ask for the
     real-money equivalent wherever purchasable currency is offered AND spent.

     This mirrors game/utils/stardustMoneyEquivalent.js; stardustMoneyEquivalentParity
     asserts the two agree. Every path returns null rather than a fallback: a
     missing figure is a visible gap, an invented one is a misleading price. */

  const CURRENCY = 'USD';
  let unitPriceMinor = null;
  let ratePromise = null;

  const isPositiveFinite = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;

  // Highest rate wins: that is what someone buying the smallest sufficient
  // pack actually pays, so a bulk tier can never understate the quote.
  function resolveUnitPriceMinor(packs) {
    if (!Array.isArray(packs) || packs.length === 0) return null;
    let highest = null;
    for (const pack of packs) {
      if (!isPositiveFinite(pack?.amount) || !isPositiveFinite(pack?.amountMinor)) return null;
      const rate = pack.amountMinor / pack.amount;
      if (!Number.isFinite(rate) || rate <= 0) return null;
      if (highest === null || rate > highest) highest = rate;
    }
    return highest;
  }

  function toMoneyMinor(stardustAmount, rate) {
    if (typeof stardustAmount !== 'number' || !Number.isFinite(stardustAmount)) return null;
    if (stardustAmount < 0) return null;
    if (!isPositiveFinite(rate)) return null;
    if (stardustAmount === 0) return 0;
    return Math.ceil(stardustAmount * rate);
  }

  function formatMoneyMinor(minorUnits, currency = CURRENCY, locale) {
    if (typeof minorUnits !== 'number' || !Number.isFinite(minorUnits) || minorUnits < 0) return null;
    const resolvedLocale = locale
      || (root && root.document && root.document.documentElement.lang)
      || 'en-US';
    try {
      return `≈ ${new Intl.NumberFormat(resolvedLocale, { style: 'currency', currency }).format(minorUnits / 100)}`;
    } catch (_) {
      return null;
    }
  }

  /** Cache the live pack catalogue's rate. Safe to call from every surface. */
  function loadReferenceRate() {
    if (unitPriceMinor !== null) return Promise.resolve(unitPriceMinor);
    if (ratePromise) return ratePromise;
    const query = root?.MoonfallCloud?.stardustDenomination?.getPublicConfig;
    if (typeof query !== 'function') return Promise.resolve(null);
    ratePromise = Promise.resolve()
      .then(() => query.call(root.MoonfallCloud.stardustDenomination))
      .then((config) => {
        if (config?.status !== 'ready') return null;
        unitPriceMinor = resolveUnitPriceMinor(config.packs);
        return unitPriceMinor;
      })
      .catch(() => null)
      .finally(() => { ratePromise = null; });
    return ratePromise;
  }

  /** "≈ $2.50", or null while the rate is unknown. */
  function moneyEquivalent(stardustAmount, options) {
    const rate = options?.unitPriceMinor ?? unitPriceMinor;
    return formatMoneyMinor(toMoneyMinor(stardustAmount, rate), CURRENCY, options?.locale);
  }

  /** "250 ✦ (≈ $25.00)", degrading to "250 ✦" when the rate is unknown. */
  function withMoney(stardustAmount, options) {
    const symbol = `${format(stardustAmount)} ✦`;
    const money = moneyEquivalent(stardustAmount, options);
    return money ? `${symbol} (${money})` : symbol;
  }

  /**
   * "250 ✦ (≈ $25.00)" that repairs itself.
   *
   * A spend surface usually renders its price before the reference rate has
   * loaded, which would otherwise leave a bare Stardust figure on screen
   * permanently. This returns the best string available now and, when the rate
   * is still missing, fetches it once and calls `rerender` so the caller can
   * redraw with the equivalent. Never invents a price: if the rate cannot be
   * resolved the display simply stays bare.
   */
  function withMoneyLive(stardustAmount, rerender, options) {
    const text = withMoney(stardustAmount, options);
    if (unitPriceMinor == null && typeof rerender === 'function') {
      loadReferenceRate().then((rate) => { if (rate) rerender(); });
    }
    return text;
  }

  return Object.freeze({
    format,
    withSymbol: (value) => `${format(value)} ✦`,
    resolveUnitPriceMinor,
    toMoneyMinor,
    formatMoneyMinor,
    loadReferenceRate,
    moneyEquivalent,
    withMoney,
    withMoneyLive,
    // Test seam: reset the cached rate between cases.
    __setUnitPriceMinor: (value) => { unitPriceMinor = value; },
  });
});

export { Stardust };
